import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

export function ClinicLogo({ clinic, className = "h-12 w-12", fallback = null }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj = null;
    let alive = true;
    setUrl(null);
    if (clinic?.logo_updated_at) {
      api.get(`/clinics/${clinic.id}/logo`, { responseType: "blob" })
        .then((r) => { if (alive) { obj = URL.createObjectURL(r.data); setUrl(obj); } })
        .catch(() => {});
    }
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [clinic?.id, clinic?.logo_updated_at]);
  if (!url) return fallback;
  return <img src={url} alt={`${clinic.name} logo`} className={`object-contain ${className}`} data-testid={`clinic-logo-${clinic.id}`} />;
}

export function LogoUploader({ clinic, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be 2 MB or smaller");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/clinics/${clinic.id}/logo`, fd);
      toast.success("Logo updated");
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/clinics/${clinic.id}/logo`); toast.success("Logo removed"); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="grid h-14 w-14 place-items-center overflow-hidden rounded-xl border border-dashed border-slate-300 bg-white">
        <ClinicLogo clinic={clinic} className="h-12 w-12" fallback={<ImagePlus className="h-5 w-5 text-slate-400" />} />
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid={`logo-file-input-${clinic.id}`} />
      <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => ref.current?.click()} data-testid={`logo-upload-button-${clinic.id}`}>
        {busy ? "Uploading…" : clinic.logo_updated_at ? "Change logo" : "Upload logo"}
      </Button>
      {clinic.logo_updated_at && <Button size="icon" variant="ghost" aria-label="Remove logo" title="Remove logo" onClick={remove} data-testid={`logo-remove-button-${clinic.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
    </div>
  );
}
